import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import NumberField from "@/components/NumberField";
import { fmtNum, fmtRiyal, toEnglishDigits } from "@/utils/toEnglishDigits";
import CustomDatePicker from "@/components/DateTimePicker";
import {
  ArrowRight,
  ChartLineUp,
  CarProfile,
  Wrench,
  Users as UsersIcon,
  Check,
  CurrencyCircleDollar,
  X as XIcon,
  ChartBar,
  MagnifyingGlass,
  IdentificationCard,
  Tag,
  CaretDown,
} from "@phosphor-icons/react";

const SORT_OPTIONS = [
  { id: "net_profit", label: "الصافي" },
  { id: "revenue", label: "الإيرادات" },
  { id: "trips_count", label: "عدد الرحلات" },
];

function StatTile({ icon: Icon, label, value, tone = "gold", highlight = false, testId }) {
  const toneCls =
    tone === "red"
      ? "text-[#F87171] border-[#F87171]/40 bg-[#F87171]/5"
      : tone === "orange"
      ? "text-[#F97316] border-[#F97316]/40 bg-[#F97316]/5"
      : tone === "green"
      ? "text-[#4ADE80] border-[#4ADE80]/40 bg-[#4ADE80]/5"
      : "text-primary border-primary/40 bg-primary/5";
  const highlightCls = highlight ? "ring-2 ring-primary/50 shadow-[0_10px_30px_-10px_rgba(201,169,97,0.4)]" : "";
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border ${toneCls} ${highlightCls} p-3 flex flex-col gap-1`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold opacity-80">
        <Icon size={12} weight="fill" />
        {label}
      </div>
      <div className="text-lg font-bold num" dir="ltr">
        {value}
      </div>
    </div>
  );
}

/* Inline consumables editor (per-plate) */
function ConsumablesEditor({ plate, initial, disabled, onSaved }) {
  const [val, setVal] = useState(initial ?? 0);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setVal(initial ?? 0);
    setDirty(false);
  }, [initial, plate]);

  const save = async () => {
    if (disabled) return;
    setSaving(true);
    try {
      await api.patch(`/admin/plate-consumables/${encodeURIComponent(plate)}`, {
        consumables_expenses: Number(val || 0),
      });
      toast.success("تم حفظ المصاريف");
      setDirty(false);
      onSaved?.(Number(val || 0));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-1.5" dir="ltr">
      <div className="w-28">
        <NumberField
          value={val}
          onChange={(v) => { setVal(v); setDirty(true); }}
          step="0.01"
          min={0}
          disabled={disabled}
          testId={`consumables-input-${plate || "empty"}`}
          className={`!py-1.5 ${dirty ? "border-[#F87171] border-dashed" : ""}`}
        />
      </div>
      {dirty && !disabled && (
        <button
          type="button"
          onClick={save}
          disabled={saving}
          title="حفظ"
          data-testid={`consumables-save-${plate}`}
          className="w-7 h-7 rounded-lg bg-[#4ADE80]/15 border border-[#4ADE80]/50 text-[#4ADE80] hover:bg-[#4ADE80]/25 flex items-center justify-center disabled:opacity-50"
        >
          <Check size={13} weight="bold" />
        </button>
      )}
    </div>
  );
}

/* Per-plate price editor row (nested inside a car type) */
function PlatePriceRow({ plate, defaultPrice, onSaved }) {
  const [val, setVal] = useState(plate.base_price ?? "");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setVal(plate.base_price ?? ""); setDirty(false); }, [plate.base_price]);

  const save = async () => {
    setSaving(true);
    try {
      const num = val === "" || val === null ? 0 : Number(val);
      await api.patch(`/admin/fleet/${encodeURIComponent(plate.plate)}`, {
        base_price: num,
      });
      const stored = num > 0 ? num : null;
      toast.success(`تم حفظ سعر ${plate.plate}`);
      setDirty(false);
      onSaved?.(plate.plate, stored);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const placeholder = defaultPrice ? `افتراضي (${defaultPrice})` : "افتراضي";

  return (
    <div
      className="flex items-center justify-between gap-2 py-2 pl-2"
      data-testid={`plate-price-row-${plate.plate}`}
    >
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 border border-primary/30 text-primary text-[11px] font-bold num shrink-0"
        dir="ltr"
      >
        <CarProfile size={11} weight="duotone" /> {plate.plate}
      </span>
      <div className="flex items-center gap-1.5" dir="ltr">
        <div className="w-28">
          <NumberField
            value={val}
            onChange={(v) => { setVal(v); setDirty(true); }}
            step="0.01"
            min={0}
            placeholder={placeholder}
            testId={`plate-price-input-${plate.plate}`}
            className={`!py-1.5 num text-right text-xs ${dirty ? "border-primary border-dashed" : ""}`}
          />
        </div>
        <span className="text-white/40 text-[10px] shrink-0">ر.س</span>
        {dirty && (
          <button
            type="button"
            onClick={save}
            disabled={saving}
            title="حفظ"
            data-testid={`plate-price-save-${plate.plate}`}
            className="w-7 h-7 rounded-lg bg-[#4ADE80]/15 border border-[#4ADE80]/50 text-[#4ADE80] hover:bg-[#4ADE80]/25 flex items-center justify-center disabled:opacity-50"
          >
            <Check size={12} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );
}

/* Base-price editor per car TYPE — expandable to reveal per-plate prices. */
function CarTypeBasePriceGroup({ car, plates, onTypeSaved, onPlateSaved }) {
  const [expanded, setExpanded] = useState(false);
  const [val, setVal] = useState(car.base_price ?? 0);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => { setVal(car.base_price ?? 0); setDirty(false); }, [car.base_price]);

  const saveType = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/cars/${encodeURIComponent(car.id)}`, {
        base_price: Number(val || 0),
      });
      toast.success(`تم حفظ سعر ${car.name_ar}`);
      setDirty(false);
      onTypeSaved?.(car.id, Number(val || 0));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const plateCount = plates.length;

  return (
    <div
      className="rounded-xl border border-white/8 bg-white/[0.02] overflow-hidden"
      data-testid={`bp-group-${car.id}`}
    >
      {/* Type-level row */}
      <div className="flex items-center justify-between gap-3 px-3 py-2.5">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            data-testid={`bp-group-toggle-${car.id}`}
            className="w-7 h-7 rounded-lg border border-white/10 text-white/60 hover:text-primary hover:border-primary/40 flex items-center justify-center shrink-0"
            title={expanded ? "طي" : "توسيع"}
          >
            <CaretDown size={13} className={`transition-transform ${expanded ? "rotate-0" : "-rotate-90"}`} />
          </button>
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/15 border border-primary/40 text-primary text-xs font-bold shrink-0">
            <CarProfile size={12} weight="duotone" /> {car.name_ar}
          </span>
          <span
            data-testid={`bp-group-count-${car.id}`}
            className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-primary text-primary-foreground text-[10px] font-bold num"
            title={`${plateCount} لوحة مسجّلة`}
          >
            {plateCount}
          </span>
        </div>
        <div className="flex items-center gap-1.5" dir="ltr">
          <div className="w-28">
            <NumberField
              value={val}
              onChange={(v) => { setVal(v); setDirty(true); }}
              step="0.01"
              min={0}
              testId={`bp-input-${car.id}`}
              className={`!py-1.5 num text-right ${dirty ? "border-primary border-dashed" : ""}`}
            />
          </div>
          <span className="text-white/50 text-[11px] shrink-0">ر.س</span>
          {dirty && (
            <button
              type="button"
              onClick={saveType}
              disabled={saving}
              title="حفظ"
              data-testid={`bp-save-${car.id}`}
              className="w-8 h-8 rounded-lg bg-[#4ADE80]/15 border border-[#4ADE80]/50 text-[#4ADE80] hover:bg-[#4ADE80]/25 flex items-center justify-center disabled:opacity-50"
            >
              <Check size={14} weight="bold" />
            </button>
          )}
        </div>
      </div>

      {/* Expanded: per-plate list */}
      {expanded && (
        <div
          className="border-t border-primary/15 bg-primary/[0.03] px-3 py-2 pr-6 border-r-2 border-r-primary/40"
          data-testid={`bp-plates-${car.id}`}
        >
          {plates.length === 0 ? (
            <div className="py-3 text-center text-white/40 text-xs italic">
              لا توجد لوحات مسجّلة تحت هذا النوع بعد. أضفها من صفحة{" "}
              <Link
                to={`/admin/fleet/${encodeURIComponent(car.id)}`}
                className="text-primary underline hover:text-[#E1C484]"
                data-testid={`bp-add-plates-link-${car.id}`}
              >
                إدارة السيارات
              </Link>
              .
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {plates.map((p) => (
                <PlatePriceRow
                  key={p.plate}
                  plate={p}
                  defaultPrice={Number(val || 0)}
                  onSaved={(plate, newPrice) => onPlateSaved?.(car.id, plate, newPrice)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function BasePricesSection() {
  const [open, setOpen] = useState(false);
  const [cars, setCars] = useState([]);
  const [plates, setPlates] = useState([]);  // all fleet vehicles across types
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [carsRes, fleetRes] = await Promise.all([
        api.get("/cars"),
        api.get("/admin/fleet"),
      ]);
      setCars((carsRes.data || []).sort((a, b) => (a.home_order || 999) - (b.home_order || 999)));
      setPlates(fleetRes.data?.items || []);
    } catch (err) {
      toast.error("تعذر جلب البيانات");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open && cars.length === 0) load(); }, [open, cars.length, load]);

  const handleTypeSaved = (id, newPrice) => {
    setCars((prev) => prev.map((c) => (c.id === id ? { ...c, base_price: newPrice } : c)));
  };
  const handlePlateSaved = (_carId, plate, newPrice) => {
    setPlates((prev) => prev.map((p) => (p.plate === plate ? { ...p, base_price: newPrice } : p)));
  };

  return (
    <div
      data-testid="base-prices-section"
      className="rounded-2xl border border-primary/25 bg-card overflow-hidden"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="base-prices-toggle"
        className="w-full flex items-center justify-between gap-3 px-4 md:px-5 py-4 hover:bg-primary/5 transition-all"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/15 border border-primary/40 flex items-center justify-center">
            <Tag size={16} weight="duotone" className="text-primary" />
          </div>
          <div className="text-right">
            <div className="text-white font-bold">السعر الأساسي لكل نوع سيارة</div>
            <div className="text-white/50 text-xs mt-0.5">
              اضغط للتوسيع وتعديل السعر — النوع أو لوحة بعينها
            </div>
          </div>
        </div>
        <CaretDown
          size={18}
          className={`text-primary/70 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="border-t border-primary/15 px-4 md:px-5 py-4">
          <p className="text-[11px] text-white/50 italic leading-relaxed mb-3 bg-white/5 rounded-lg p-2.5">
            السعر الأساسي مثبّت من قِبل الأدمن. يمكنك تعديل السعر على مستوى نوع السيارة (افتراضي)، أو تحديد سعر خاص لكل لوحة مسجّلة.
          </p>
          {loading ? (
            <div className="text-center py-8 text-primary/70 animate-pulse">جارٍ التحميل…</div>
          ) : cars.length === 0 ? (
            <div className="text-center py-6 text-white/40 text-sm">لا توجد أنواع سيارات</div>
          ) : (
            <div className="space-y-2">
              {cars.map((c) => {
                const carPlates = plates.filter((p) => p.car_type === c.id);
                return (
                  <CarTypeBasePriceGroup
                    key={c.id}
                    car={c}
                    plates={carPlates}
                    onTypeSaved={handleTypeSaved}
                    onPlateSaved={handlePlateSaved}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────  Main Page  ──────────────────────── */
export default function CarProfit() {
  const navigate = useNavigate();
  const [dfrom, setDfrom] = useState("");
  const [dto, setDto] = useState("");
  const [carTypeFilter, setCarTypeFilter] = useState("all");
  const [plateQuery, setPlateQuery] = useState("");
  const [sortBy, setSortBy] = useState("net_profit");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], totals: {} });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dfrom) params.dfrom = dfrom;
      if (dto) params.dto = dto;
      const res = await api.get("/admin/reports/car-profit", { params });
      setData(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [dfrom, dto]);

  useEffect(() => { load(); }, [load]);

  // Derived: unique car types for filter chips
  const carTypes = useMemo(() => {
    const map = new Map();
    (data.items || []).forEach((it) => {
      if (it.car_type) map.set(it.car_type, it.car_name_ar || it.car_type);
    });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [data.items]);

  const filteredSorted = useMemo(() => {
    let list = data.items || [];
    if (carTypeFilter !== "all") list = list.filter((it) => it.car_type === carTypeFilter);
    const q = plateQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((it) => (it.plate || "").toLowerCase().includes(q));
    }
    return [...list].sort((a, b) => (Number(b[sortBy]) || 0) - (Number(a[sortBy]) || 0));
  }, [data.items, carTypeFilter, plateQuery, sortBy]);

  // Filtered totals (respects car type filter)
  const filteredTotals = useMemo(() => {
    const t = { revenue: 0, driver_wages: 0, consumables_expenses: 0, trips_count: 0 };
    filteredSorted.forEach((it) => {
      t.revenue += Number(it.revenue) || 0;
      t.driver_wages += Number(it.driver_wages) || 0;
      t.consumables_expenses += Number(it.consumables_expenses) || 0;
      t.trips_count += Number(it.trips_count) || 0;
    });
    t.net_profit = t.revenue - t.driver_wages - t.consumables_expenses;
    return t;
  }, [filteredSorted]);

  const handleConsumablesSaved = (plate, newVal) => {
    setData((d) => ({
      ...d,
      items: (d.items || []).map((it) =>
        it.plate === plate
          ? {
              ...it,
              consumables_expenses: newVal,
              net_profit: (Number(it.revenue) || 0) - (Number(it.driver_wages) || 0) - newVal,
            }
          : it,
      ),
    }));
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-6xl mx-auto px-4 py-6 space-y-5">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            data-testid="cp-back-btn"
            className="w-10 h-10 rounded-xl border border-white/15 text-white/70 hover:text-white hover:bg-white/5 flex items-center justify-center"
            title="رجوع"
          >
            <ArrowRight size={18} weight="bold" />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-primary/15 border border-primary/40 flex items-center justify-center">
              <ChartLineUp size={22} weight="duotone" className="text-primary" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white">فرز أرباح السيارات</h1>
              <p className="text-white/50 text-xs mt-0.5">حسب اللوحة · أرباح كل سيارة على حدة</p>
            </div>
          </div>
        </div>

        {/* Filters card */}
        <div className="rounded-2xl border border-primary/25 bg-card p-4 md:p-5 space-y-4" data-testid="cp-filters">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-white/60 mb-1">من تاريخ</label>
              <div className="flex items-center gap-1">
                <div className="flex-1">
                  <CustomDatePicker value={dfrom} onChange={setDfrom} testId="cp-dfrom" />
                </div>
                {dfrom && (
                  <button type="button" onClick={() => setDfrom("")} className="w-10 h-10 rounded-xl border border-white/15 text-white/50 hover:text-white flex items-center justify-center" title="مسح">
                    <XIcon size={14} />
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1">إلى تاريخ</label>
              <div className="flex items-center gap-1">
                <div className="flex-1">
                  <CustomDatePicker value={dto} onChange={setDto} testId="cp-dto" />
                </div>
                {dto && (
                  <button type="button" onClick={() => setDto("")} className="w-10 h-10 rounded-xl border border-white/15 text-white/50 hover:text-white flex items-center justify-center" title="مسح">
                    <XIcon size={14} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Plate search */}
          <div>
            <label className="block text-xs text-white/60 mb-1 flex items-center gap-1">
              <IdentificationCard size={12} className="text-primary/70" /> بحث برقم اللوحة
            </label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                type="text"
                value={plateQuery}
                onChange={(e) => setPlateQuery(e.target.value)}
                placeholder="مثال: أ ب ج أو 1234"
                data-testid="cp-plate-search"
                className="mz-input w-full text-right pr-9 num"
              />
              {plateQuery && (
                <button
                  type="button"
                  onClick={() => setPlateQuery("")}
                  data-testid="cp-plate-clear"
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white/5 border border-white/15 text-white/50 hover:text-white flex items-center justify-center"
                  title="مسح"
                >
                  <XIcon size={11} />
                </button>
              )}
            </div>
          </div>

          {/* Car type filter chips */}
          {carTypes.length > 0 && (
            <div>
              <div className="text-xs text-white/60 mb-2">نوع السيارة</div>
              <div className="flex flex-wrap gap-2" data-testid="cp-car-chips">
                {[{ id: "all", label: "الكل" }, ...carTypes].map((c) => {
                  const active = carTypeFilter === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCarTypeFilter(c.id)}
                      data-testid={`cp-car-chip-${c.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                        active
                          ? "bg-primary text-primary-foreground border-primary font-bold"
                          : "bg-primary/5 border-primary/30 text-white hover:bg-primary/15"
                      }`}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Sort chips */}
          <div>
            <div className="text-xs text-white/60 mb-2">ترتيب حسب</div>
            <div className="flex flex-wrap gap-2" data-testid="cp-sort-chips">
              {SORT_OPTIONS.map((s) => {
                const active = sortBy === s.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setSortBy(s.id)}
                    data-testid={`cp-sort-${s.id}`}
                    className={`px-3 py-1.5 rounded-full text-xs border transition-all ${
                      active
                        ? "bg-primary text-primary-foreground border-primary font-bold"
                        : "bg-white/5 border-white/20 text-white/70 hover:bg-white/10"
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Base prices per car type (collapsible) */}
        <BasePricesSection />

        {/* Grand total card */}
        <div
          className="rounded-2xl border-2 border-primary/50 bg-gradient-to-br from-[#0F2148] to-[#172956] p-4 md:p-5"
          data-testid="cp-grand-total"
        >
          <div className="flex items-center gap-2 mb-3">
            <ChartBar size={18} weight="duotone" className="text-primary" />
            <h2 className="text-primary font-bold text-lg">الإجمالي العام</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <StatTile icon={CurrencyCircleDollar} label="الإيرادات" value={fmtRiyal(filteredTotals.revenue)} tone="gold" testId="cp-total-revenue" />
            <StatTile icon={UsersIcon} label="أجور السائقين" value={fmtRiyal(filteredTotals.driver_wages)} tone="orange" testId="cp-total-wages" />
            <StatTile icon={Wrench} label="الاستهلاكية" value={fmtRiyal(filteredTotals.consumables_expenses)} tone="red" testId="cp-total-consumables" />
            <StatTile
              icon={ChartLineUp}
              label="الصافي"
              value={fmtRiyal(filteredTotals.net_profit)}
              tone={filteredTotals.net_profit >= 0 ? "green" : "red"}
              highlight
              testId="cp-total-net"
            />
          </div>
          <div className="text-white/50 text-xs mt-3 flex items-center gap-1">
            <span>عدد الرحلات:</span>
            <span className="num text-white/80 font-bold">{fmtNum(filteredTotals.trips_count)}</span>
          </div>
        </div>

        {/* Per-plate rows */}
        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
        ) : filteredSorted.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-white/15 py-16 text-center" data-testid="cp-empty">
            <ChartLineUp size={56} weight="duotone" className="text-white/25 mx-auto mb-3" />
            <h3 className="text-white/70 font-bold">لا توجد بيانات للفلاتر المحددة</h3>
            <p className="text-white/40 text-sm mt-1">جرّب تغيير النطاق الزمني أو نوع السيارة</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSorted.map((it) => {
              const isNoPlate = !it.plate;
              const negative = (Number(it.net_profit) || 0) < 0;
              const driverText = it.drivers?.length
                ? it.drivers.length > 3
                  ? `${it.drivers.slice(0, 2).join("، ")} +${it.drivers.length - 2}`
                  : it.drivers.join("، ")
                : "—";
              return (
                <div
                  key={it.plate || "__none__"}
                  data-testid={`cp-row-${it.plate || "none"}`}
                  className={`rounded-2xl border p-4 md:p-5 transition-all ${
                    negative
                      ? "border-[#F87171]/60 bg-[#F87171]/5"
                      : "border-primary/20 bg-card hover:border-primary/40"
                  }`}
                >
                  {/* Top row */}
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-bold text-sm num shrink-0 ${
                          isNoPlate
                            ? "bg-white/5 border border-white/25 text-white/50"
                            : "bg-primary/20 border border-primary/50 text-primary"
                        }`}
                        dir="ltr"
                      >
                        <CarProfile size={13} weight="duotone" /> {isNoPlate ? "بدون لوحة" : it.plate}
                      </span>
                      <div className="min-w-0">
                        <div className="text-white text-sm font-bold truncate">
                          {it.car_name_ar || it.car_type || "غير محدد"}
                        </div>
                        <div className="text-white/50 text-xs mt-0.5 flex items-center gap-1 truncate">
                          <UsersIcon size={11} weight="duotone" className="shrink-0" />
                          {driverText}
                        </div>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-primary/15 border border-primary/30 text-primary text-[11px] font-bold num" dir="ltr">
                      {fmtNum(it.trips_count)} رحلة
                    </span>
                  </div>

                  {/* Numbers */}
                  <div className="mt-4 space-y-2 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-white/60">الإيرادات:</span>
                      <span className="text-primary font-bold num" dir="ltr">+{fmtRiyal(it.revenue)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-white/60">أجور السائقين:</span>
                      <span className="text-[#F97316] font-bold num" dir="ltr">−{fmtRiyal(it.driver_wages)}</span>
                    </div>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-white/60 flex items-center gap-1.5">
                        <Wrench size={12} className="text-[#F87171]" /> الاستهلاكية:
                      </span>
                      <ConsumablesEditor
                        plate={it.plate}
                        initial={it.consumables_expenses}
                        disabled={isNoPlate}
                        onSaved={(v) => handleConsumablesSaved(it.plate, v)}
                      />
                    </div>
                  </div>

                  {/* Net */}
                  <div className="mt-4 pt-4 border-t border-primary/10 flex items-center justify-between">
                    <span className="text-white/70 font-bold">الصافي:</span>
                    <span
                      className={`text-2xl font-bold num ${negative ? "text-[#F87171]" : "text-[#4ADE80]"}`}
                      dir="ltr"
                      data-testid={`cp-net-${it.plate || "none"}`}
                    >
                      {fmtRiyal(it.net_profit)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
